import { useEffect, useRef, useState, type PointerEvent, type ReactNode } from 'react';

import { onBackButton } from '../telegram';

/** Past a third of the sheet's own height, a drag down is a dismissal. */
const CLOSING_SHARE = 1 / 3;

/**
 * …and so is a flick, in pixels per millisecond, however short it was.
 *
 * 0.5 in the old app, which took a throw few thumbs make; 0.11 since C-016 (FND-1611), the
 * figure Vitali picked on the motion playground, so a short quick stroke down is enough.
 */
const CLOSING_SPEED = 0.11;

/**
 * A finger that stopped this long before lifting has no speed left. C-016: the speed is
 * the last step's, and a finger resting on the glass sends no further steps, so without
 * this a slow drag that paused would still carry the speed of its last move.
 */
const RESTED_MS = 100;

/** Below this a finger has not moved, it has rested: the strip is still tapped. */
const MOVED = 4;

/**
 * How long the sheet takes to come up, and to go back down.
 *
 * The stylesheet runs both - a keyframe in and a transition on `.sheet--going`
 * out, the arrangement `.undo-bar` already uses. These two numbers are here
 * because the mounting has to outlast the second one: without that the sheet
 * would be taken out of the DOM in the frame it was asked to close and there
 * would be nothing left to run the closing on.
 */
const OPEN_MS = 300;
/** Leaving, and it is always the quicker of the two. */
const CLOSE_MS = 220;

/**
 * A form that stands over the screen it was opened from rather than inside it.
 *
 * The add-a-gathering form was a card in the middle of the schedule with its
 * buttons pinned to the bottom of the phone - the arrangement `.dock`'s comment
 * in the stylesheet records the objection to. With the date wheel open it
 * stopped fitting: the card pushed the schedule down and «Сохранить» left the
 * screen with it. A card taller than the screen is a screen, and this is that
 * screen without a level of navigation under it - the typical case is two taps,
 * and a transition for two taps is a transition nobody asked for.
 *
 * It knows nothing about what is in it. Everything here is the container: the
 * darkening, the handle, the cap on its height, and the three ways out.
 */
export default function Sheet({
  open,
  label,
  onClose,
  foot,
  children,
}: {
  open: boolean;
  /** What the sheet is, for a reader who cannot see it. */
  label: string;
  onClose: () => void;
  /**
   * What finishes the form: its button, and the refusal that belongs directly
   * over it. Welded to the sheet's bottom edge rather than to the end of its
   * content - on a sheet taller than its cap the button used to be below the
   * fold, so the way out of the form had to be scrolled to.
   *
   * The pair travels together and that is why this is one slot rather than a
   * rule about the last button: a refusal is read where the tap was made, and a
   * sticky button with its refusal left behind in the scroll would say
   * «На это время уже есть спевка» somewhere nobody is looking.
   */
  foot?: ReactNode;
  children: ReactNode;
}) {
  /**
   * Whether the sheet is in the DOM. It outlives `open` by one closing run.
   *
   * The same shape as `Unfold`, and for the same reason: what asks a sheet to
   * close is a state change in the caller, and a caller that stops rendering it
   * in that frame leaves nothing to animate. Arriving was seen and leaving
   * happened between frames, which is the asymmetry the fold was cured of on
   * 2026-08-21.
   */
  const [mounted, setMounted] = useState(open);
  /**
   * The contents as they were while it was open. What closes a sheet is often
   * the disappearance of what was in it - a question answered, a row that is no
   * longer being edited - and without this the sheet would go down empty, which
   * is the same jerk from the other end.
   */
  const held = useRef(children);
  const heldFoot = useRef(foot);
  if (open) {
    held.current = children;
    heldFoot.current = foot;
  }

  useEffect(() => {
    if (open) {
      setMounted(true);
      return;
    }
    if (!mounted) return;
    const timer = window.setTimeout(() => setMounted(false), CLOSE_MS);
    return () => window.clearTimeout(timer);
  }, [open, mounted]);

  // Everything below belongs to a sheet that is on the screen - the back button
  // it takes over, the history entry it adds, the finger it is following. Tying
  // that to a mount rather than branching inside each effect is what keeps the
  // closed state genuinely empty.
  return mounted ? (
    <Open going={!open} label={label} onClose={onClose} foot={heldFoot.current}>
      {held.current}
    </Open>
  ) : null;
}

/**
 * The page, held still for as long as any sheet is on the screen.
 *
 * This was here for one evening, 2026-08-24, against a different fault - a form
 * that flew to the top of the screen when the keyboard came up - and was taken
 * out because it did not cure that one and cost something: pinning the body
 * stops the document scrolling, and what moved the sheet then was the client
 * sliding the layout viewport, which no CSS refuses. Worse, it took away a
 * touch on the screen, which was the only thing making the client report a
 * viewport at all.
 *
 * Both halves of that have expired. The fault is a different one - «фон всё ещё
 * едет под листом», the owner, 2026-09-02, and that is the document scrolling
 * and nothing else - and the touch is no longer needed, because `viewport.ts`
 * measures on a schedule of its own after a focus rather than waiting to be
 * told.
 *
 * It replaces a rule per element, which is what the last two rounds were:
 * `touch-action: none` on the darkening, then the same on the sheet's foot,
 * each after a drag was found that had somewhere else to go. The handle, the
 * gaps between the blocks, the sheet's own padding and whatever is added to it
 * next are all still such places. A page that cannot scroll has none of them,
 * and the guarantee stops depending on somebody remembering the rule.
 *
 * Counted rather than a flag, because a sheet can stand over a sheet - the
 * rota's «Кого поставить» over a screen that is itself in one - and the first
 * of the two to close must not hand the page back while the second is still up.
 */
let sheets = 0;
/** Where the page was when the first of them went up. */
let held = 0;

function pin(): () => void {
  if (sheets === 0) {
    held = window.scrollY;
    const { style } = document.body;
    // `left` and `right` as well as `position`: a fixed box shrink-wraps its
    // content, and a body that narrows takes every screen behind the sheet with
    // it - visibly, because the darkening is see-through.
    style.position = 'fixed';
    style.top = `${-held}px`;
    style.left = '0';
    style.right = '0';
  }
  sheets += 1;
  return () => {
    sheets -= 1;
    if (sheets > 0) return;
    const { style } = document.body;
    style.position = '';
    style.top = '';
    style.left = '';
    style.right = '';
    // Synchronously, in the same frame the body stops being fixed: a page that
    // is handed back at the top and scrolled a frame later is a page that
    // jumps, and it jumps exactly where the sheet has just left.
    window.scrollTo(0, held);
  };
}

function Open({
  going,
  label,
  onClose,
  foot,
  children,
}: {
  /** The sheet has been asked to close and is on its way down. */
  going: boolean;
  label: string;
  onClose: () => void;
  foot?: ReactNode;
  children: ReactNode;
}) {
  const sheet = useRef<HTMLElement>(null);
  const grip = useRef<HTMLButtonElement>(null);
  /** How far down the finger has taken the sheet. 0 unless one is on it. */
  const [shift, setShift] = useState(0);
  const held = useRef<Grab | null>(null);
  /** Whether the pointer that is about to become a click had travelled. */
  const travelled = useRef(false);

  // Read inside listeners attached once, and the caller is free to hand over a
  // new closure on every render.
  const close = useRef(onClose);
  close.current = onClose;

  // The way back, and on Android that is the phone's own key: the client routes
  // it to this event for as long as the button is shown, which is exactly as
  // long as the sheet is up.
  //
  // The browser's own history is deliberately not touched. An entry pushed on
  // opening has to be popped again on every other way out, and inside a client
  // that runs the app in an iframe a stray `history.back()` is the whole of
  // Telegram going back rather than the sheet. The two gestures are the answer
  // outside Telegram, where there is no such key to press anyway.
  useEffect(() => onBackButton(() => close.current()), []);

  // The sheet is over the screen, so the keyboard has to be in it: without this
  // Tab walks the whole schedule behind the darkening first. The dialog itself
  // takes it, and the handle - the first thing inside, and the way out for
  // somebody who cannot make any of the three gestures - is one Tab away.
  //
  // The handle used to take it, and that is what drew «две параллельные жёлтые
  // линии» across the top of every sheet the owner opened: `.sheet-grip` is a
  // full-width button sixteen pixels tall, `:focus-visible` matched a focus
  // nobody had asked for with a keyboard, and a two-pixel ring around a box
  // that wide is a pair of lines with its sides off the screen. A container
  // focused programmatically does not match that selector, which is the whole
  // reason the dialog is the better holder - and it reads its own label to a
  // screen reader rather than «Закрыть».
  //
  // At the end of the arriving run and not at the start of it: the client
  // scrolls to what has just taken the focus, and at the start of the run the
  // sheet is still below the bottom edge of the screen. And not at all if it
  // was sent away inside those 300ms - a sheet on its way back down is the last
  // thing to scroll to.
  const leaving = useRef(going);
  leaving.current = going;
  useEffect(() => {
    const timer = window.setTimeout(() => {
      if (!leaving.current) sheet.current?.focus();
    }, OPEN_MS);
    return () => window.clearTimeout(timer);
  }, []);

  // And the page is pinned for as long as it is up. See `pin`.
  useEffect(pin, []);

  /**
   * Whatever has the focus is kept inside the sheet, and the sheet does it.
   *
   * The client used to: iOS reveals a focused field by scrolling the document,
   * and until the page was pinned that scroll dragged the whole fixed sheet up
   * the screen - which is the flying form `viewport.ts` was written against. A
   * pinned page has no scroll to give, so the reveal stopped happening at all
   * and the last field of a tall sheet sat on the bottom edge with «Сохранить»
   * over it. Measured at 375x812 with a 336 keyboard: «Название, если нужно» at
   * 388..434 against a body that ends at 414.
   *
   * `block: 'nearest'` and not `center`: the scroller is `.sheet-body`, whose
   * box already ends where the foot begins, so the least that puts the field
   * inside it is also exactly clear of the button. It does nothing at all to a
   * field that is already whole, which is what makes it safe to call on every
   * resize.
   *
   * A frame after the focus rather than in it. `viewport.ts` listens on the
   * document and this listens on the sheet, so this one runs first - `focusin`
   * bubbles - and the box the field has to fit inside is the one that listener
   * has not written yet.
   *
   * And again whenever the sheet's own box changes, which is the same event
   * from the other end: the keyboard arriving, the keyboard leaving, a wheel
   * unfolding under the field. One observer says all of them.
   */
  useEffect(() => {
    const node = sheet.current;
    if (node === null) return;
    const reveal = (): void => {
      const focused = document.activeElement;
      if (focused instanceof HTMLElement && node.contains(focused)) {
        focused.scrollIntoView({ block: 'nearest' });
      }
    };
    const onFocusIn = (): void => {
      window.requestAnimationFrame(reveal);
    };
    node.addEventListener('focusin', onFocusIn);
    const watcher = new ResizeObserver(reveal);
    watcher.observe(node);
    return () => {
      node.removeEventListener('focusin', onFocusIn);
      watcher.disconnect();
    };
  }, []);

  function grabbed(event: PointerEvent<HTMLElement>): void {
    held.current = {
      pointer: event.pointerId,
      from: event.clientY,
      y: event.clientY,
      at: Date.now(),
      speed: 0,
      moved: false,
    };
    event.currentTarget.setPointerCapture(event.pointerId);
  }

  function dragged(event: PointerEvent<HTMLElement>): void {
    const grab = held.current;
    if (grab === null || grab.pointer !== event.pointerId) return;
    const now = Date.now();
    // The speed of the last step and not of the whole drag: a finger that
    // travelled fast and then stopped before lifting is asking the sheet to
    // stay, and the average over the drag would say the opposite.
    if (now > grab.at) grab.speed = (event.clientY - grab.y) / (now - grab.at);
    grab.y = event.clientY;
    grab.at = now;
    // Down only. Upwards there is nothing to drag towards: the sheet is already
    // as tall as it is allowed to be.
    const distance = Math.max(0, event.clientY - grab.from);
    if (distance > MOVED) grab.moved = true;
    setShift(distance);
  }

  function dropped(event: PointerEvent<HTMLElement>): void {
    const grab = held.current;
    if (grab === null || grab.pointer !== event.pointerId) return;
    held.current = null;
    // The click this pointer is about to fire arrives after this handler, so
    // what it needs to know outlives the grab.
    travelled.current = grab.moved;
    setShift(0);
    const distance = Math.max(0, event.clientY - grab.from);
    const height = sheet.current?.getBoundingClientRect().height ?? 0;
    const speed = Date.now() - grab.at > RESTED_MS ? 0 : grab.speed;
    if (distance > height * CLOSING_SHARE || (grab.moved && speed > CLOSING_SPEED)) close.current();
  }

  function cancelled(): void {
    held.current = null;
    travelled.current = false;
    setShift(0);
  }

  return (
    <>
      {/* Not a control and not in the tab order: it is the screen behind, seen
          through the fact that it is not the screen any more. The handle is
          what answers a keyboard. */}
      <div
        className={going ? 'sheet-scrim sheet-scrim--going' : 'sheet-scrim'}
        onClick={() => close.current()}
      />
      <section
        ref={sheet}
        className={going ? 'sheet sheet--going' : 'sheet'}
        role="dialog"
        aria-modal="true"
        aria-label={label}
        // So it can hold the focus on the way in. Never in the tab order:
        // -1 is «focusable, not tabbable», so Tab still starts at the handle.
        tabIndex={-1}
        onKeyDown={(event) => {
          if (event.key === 'Escape') close.current();
        }}
        style={shift === 0 ? undefined : { transform: `translateY(${shift}px)` }}
      >
        <button
          ref={grip}
          className="sheet-grip"
          data-press="none"
          type="button"
          aria-label="Закрыть"
          onPointerDown={grabbed}
          onPointerMove={dragged}
          onPointerUp={dropped}
          onPointerCancel={cancelled}
          // A drag that did not go far enough ends on this element, and a
          // pointer that went down and up on a button is a click. Without this
          // every drag the sheet survived would close it anyway.
          onClick={() => {
            const wasDrag = travelled.current;
            travelled.current = false;
            if (!wasDrag) close.current();
          }}
        >
          <span className="sheet-grip-bar" />
        </button>
        <div className="sheet-body">{children}</div>
        {foot !== undefined && <div className="sheet-foot">{foot}</div>}
      </section>
    </>
  );
}

/** A finger on the handle: where it started, where it was last, and how fast. */
type Grab = {
  pointer: number;
  from: number;
  y: number;
  at: number;
  speed: number;
  moved: boolean;
};
